/**
 * Turns held and tapped directions into one `Input` per scan (docs/spec/07 §3): of the directions
 * held, the most recently pressed wins; a tap that came and went between two scans still counts once.
 * Pure: the sources (keys, pad, touch) call `press` and `release` with their own ids.
 */

import type { Dir, Input } from '../sim/types';
import { DIR_NONE } from '../sim/types';

export class DirectionLatch {
  /** Held sources, oldest first: [source id, direction]. */
  private held: [string, Dir][] = [];
  private tapped: Dir = DIR_NONE;
  private fireSources = new Set<string>();
  private fireTapped = false;

  press(source: string, dir: Dir): void {
    this.held = this.held.filter(([s]) => s !== source);
    this.held.push([source, dir]);
    this.tapped = dir;
  }

  release(source: string): void {
    this.held = this.held.filter(([s]) => s !== source);
  }

  fireDown(source: string): void {
    this.fireSources.add(source);
    this.fireTapped = true;
  }

  fireUp(source: string): void {
    this.fireSources.delete(source);
  }

  /** Forget everything held (the window lost focus, a menu opened). */
  reset(): void {
    this.held = [];
    this.tapped = DIR_NONE;
    this.fireSources.clear();
    this.fireTapped = false;
  }

  /** The direction held right now (no latch). */
  current(): Dir {
    return this.held[this.held.length - 1]?.[1] ?? DIR_NONE;
  }

  get fireHeld(): boolean {
    return this.fireSources.size > 0;
  }

  /** The input for the scan about to run; clears the taps. */
  take(): Input {
    const dir = this.held[this.held.length - 1]?.[1] ?? this.tapped;
    const fire = this.fireSources.size > 0 || this.fireTapped;
    this.tapped = DIR_NONE;
    this.fireTapped = false;
    return { dir, fire };
  }
}
